import { configureStore } from '@reduxjs/toolkit'
import type { TypedUseSelectorHook } from 'react-redux'
import { useDispatch, useSelector } from 'react-redux'
import { combineReducers } from 'redux'
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist'
import storage from 'redux-persist/lib/storage'

import catalogStore from '@/core/store/catalog.store'
import uiState from '@/core/store/ui.store'

const rootReducer = combineReducers({
  catalogStore: persistReducer(
    {
      key: 'catalog',
      storage: storage,
      whitelist: [
        'catalogType',
        'playedAudioIds',
        'cleverMapPlayedObjectIds',
        'isPayedClient',
        'gotTutorial',
        'payedTours',
      ],
    },
    catalogStore,
  ),
  uiState,
})

// const rootReducer = combineReducers({
//   catalogStore,
//   uiState,
// })

// Минималистичный logger middleware с информацией о месте вызова (только для development)
const loggerMiddleware = (store: any) => (next: any) => (action: any) => {
  if (process.env.NODE_ENV !== 'production') {
    // Получаем stack trace для определения места вызова
    const stack = new Error().stack
    let caller = '???'

    if (stack) {
      const lines = stack.split('\n')
      // Ищем первую строку, которая не содержит store.ts, redux или node_modules
      for (let i = 2; i < lines.length; i++) {
        const line = lines[i]
        if (
          !line.includes('store.ts') &&
          !line.includes('redux') &&
          !line.includes('node_modules')
        ) {
          // Извлекаем имя файла и строку из webpack-internal формата
          // Пример: webpack-internal:///./src/components/Header.tsx:95
          const webpackMatch = line.match(/webpack-internal:\/\/\/\.\/src\/(.*?):(\d+)/)
          if (webpackMatch) {
            const [, filePath, lineNum] = webpackMatch
            // Берем только имя файла без полного пути
            const fileName = filePath.split('/').pop() || filePath
            caller = `${fileName}:${lineNum}`
            break
          }

          // Обычный формат (для production builds)
          const match = line.match(/(?:at\s+)?(?:.*?\s+)?\(?(.*?):(\d+):(\d+)\)?/)
          if (match) {
            const [, filePath, lineNum] = match
            const fileName = filePath.split('/').pop() || filePath
            caller = `${fileName}:${lineNum}`
            break
          }
        }
      }
    }

    const payloadStr = action.payload !== undefined
      ? ` | ${JSON.stringify(action.payload)}`
      : ''

    console.log(`[Redux] ${action.type} | ${caller}${payloadStr}`)
  }
  return next(action)
}

const middlewares = (getDefaultMiddleware: any) => {
  const middleware = getDefaultMiddleware({
    serializableCheck: {
      ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
    },
  })

  // Добавляем logger только в development
  if (process.env.NODE_ENV !== 'production') {
    return middleware.concat(loggerMiddleware)
  }

  return middleware
}

export const store = configureStore({
  reducer: rootReducer,
  middleware: middlewares,
})

export const persistor = persistStore(store)

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

// Правильная типизация для работы с async thunks
export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector
